import { useCallback, useEffect, useRef, useState } from "react";
import {
  DisconnectReason,
  Room,
  RoomEvent,
  createLocalAudioTrack,
  type LocalAudioTrack,
  type RemoteTrack,
} from "livekit-client";

import type { CallAudioGrant, EngagementTier } from "../types";
import { markCallActive, markCallInactive } from "../../../lib/call-guard";

/**
 * One supervisor's audio connection to one live call.
 *
 * Deliberately one call at a time. A supervisor watching twenty calls must never be in
 * twenty LiveKit rooms — that is twenty peer connections and twenty audio decodes in a
 * single browser tab, and it is what makes multi-call supervision impractical. Watching
 * stays text; hearing is a deliberate, single-call action.
 *
 * Upgrading from listening to taking over DOES drop and rejoin: the two grants carry
 * different publish rights, and LiveKit fixes those at join. The gap is ~1.7s, and it lands
 * inside the window where the agent is speaking its hand-off line, so the supervisor is
 * back before there is anything to hear. Worth knowing before assuming the room is continuous.
 *
 * Owned by one page-level provider, never by a component keyed to the selected call --
 * see `audio-provider.tsx`.
 */
/**
 * Prove the microphone works *before* anything is asked of the agent.
 *
 * Ordering matters more than it looks. The server takes over by telling the agent to stall,
 * then to hold -- and only then mints a token. If the browser discovers a denied microphone
 * after that, the AI is already silent and the customer is sitting in silence with nobody
 * able to speak. Failing here costs an error message and nothing else.
 */
export async function preflightMicrophone(): Promise<void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  // Release it immediately; publishing opens its own track. Holding this one would leave
  // the tab's recording indicator lit while merely listening.
  stream.getTracks().forEach((track) => track.stop());
}

export function useCallAudio() {
  const roomRef = useRef<Room | null>(null);
  const micRef = useRef<LocalAudioTrack | null>(null);
  const audioElementsRef = useRef<HTMLAudioElement[]>([]);

  const [tier, setTier] = useState<EngagementTier>("watch");
  const [connectedCallId, setConnectedCallId] = useState<string | null>(null);
  const [micLive, setMicLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const teardown = useCallback(async () => {
    micRef.current?.stop();
    micRef.current = null;
    // Remove the elements we attached, or their audio keeps playing after the room is
    // gone and the operator hears a call they think they left.
    audioElementsRef.current.forEach((element) => {
      element.pause();
      element.srcObject = null;
      element.remove();
    });
    audioElementsRef.current = [];

    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      await room.disconnect().catch(() => undefined);
      markCallInactive(); // only if there was actually a connected room to end
    }

    setMicLive(false);
    setTier("watch");
    setConnectedCallId(null);
  }, []);

  const connect = useCallback(
    async (grant: CallAudioGrant, callId: string) => {
      setError(null);
      await teardown();

      const room = new Room();
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind !== "audio") return;
        const element = track.attach() as HTMLAudioElement;
        element.autoplay = true;
        // Kept out of the layout: this is audio, and a visible <audio> control would
        // invite an operator to pause a live call they are supposed to be monitoring.
        element.style.display = "none";
        document.body.appendChild(element);
        audioElementsRef.current.push(element);
      });

      // Every server-side end of a session arrives here and nowhere else: an eviction by
      // the media re-authorisation sweep, a duplicate identity kicking the earlier join,
      // a token expiring, a plain network drop. They are indistinguishable in the UI and
      // were indistinguishable in the logs too, so say which one it was.
      room.on(RoomEvent.Disconnected, (reason) => {
        console.warn(
          `supervision: audio disconnected call=${callId} reason=${
            reason === undefined ? "unknown" : DisconnectReason[reason] ?? reason
          }`,
        );
        if (roomRef.current === room) void teardown();
      });

      await room.connect(grant.ws_url, grant.token);
      if (roomRef.current !== room) {
        await room.disconnect().catch(() => undefined);
        return;
      }
      markCallActive();
      setConnectedCallId(callId);
      setTier(grant.can_publish ? "takeover" : "listen");

      if (grant.can_publish) {
        try {
          const mic = await createLocalAudioTrack();
          if (roomRef.current !== room) { mic.stop(); return; }
          micRef.current = mic;
          await room.localParticipant.publishTrack(mic);
          if (roomRef.current !== room) { mic.stop(); return; }
          setMicLive(true);
        } catch (caught) {
          // By the time a publish token exists the agent is ALREADY on hold. Merely
          // reporting the error here left the session in takeover with nobody able to
          // speak, and the heartbeat then kept extending that hold -- the exact silent
          // call the hold was supposed to prevent. Tear down and rethrow so the caller
          // hands the call straight back to the AI.
          await teardown();
          throw new Error(
            (caught as Error)?.message ||
              "Could not access your microphone — the caller cannot hear you.",
          );
        }
      }
      return room;
    },
    [teardown],
  );

  const setMicEnabled = useCallback(async (enabled: boolean) => {
    const mic = micRef.current;
    if (!mic) return;
    await (enabled ? mic.unmute() : mic.mute());
    setMicLive(enabled);
  }, []);

  // Leaving the console must not leave a connection open. The agent-side hold expires on
  // its own, so a dropped supervisor hands the call back rather than stranding it.
  useEffect(() => () => void teardown(), [teardown]);

  return { tier, connectedCallId, micLive, error, connect, teardown, setMicEnabled, setError };
}
